import { useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { PageLayout, PageSize, Spinner } from "@/components";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import { Col, Row } from "@/components/customComponents";
import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";

import { EditVoiceDetail } from "./components/EditVoiceDetail";
import { ViewVoiceDetail } from "./components/ViewVoiceDetail";
import { useVoiceDetail } from "./hooks/useVoiceDetail";

export function VoiceDetail() {
  const { voiceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { hasFeatureFlag: hasTTS, isLoading: isFeatureFlagLoading } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const localizedPath = useLocalizedPathname();

  const shouldEdit = searchParams.get("edit") === "true";
  const [stateIsEditing, setStateIsEditing] = useState(shouldEdit);
  const { voice, isLoading, isAudioLoading, sampleText, isPlaying, handleSpeak, handleStop, updateVoice } = useVoiceDetail(voiceId!, stateIsEditing);

  useEffect(() => {
    if (shouldEdit) {
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, shouldEdit]);
  const handleEdit = useCallback(() => {
    setStateIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setStateIsEditing(false);
  }, []);

  const handleSave = useCallback(
    async (payload: { name: string; description: string }) => {
      const success = await updateVoice(payload);
      if (success) {
        setStateIsEditing(false);
      }
    },
    [updateVoice],
  );

  if (isFeatureFlagLoading || isLoading) {
    return <Spinner centerToScreen={true} />;
  }

  if (!hasTTS) {
    return <Navigate to="/" />;
  }

  return (
    <PageLayout size={PageSize.Medium}>
      <Row className="mb-2">
        <Col>
          <NavigateBackButtonV2 text={t("voice:navigation.backToMyVoices")} path={localizedPath(ROUTES.voices)} />
        </Col>
      </Row>
      <div className="flex flex-col relative bg-gray-1 dark:bg-gray-3 p-lg max-mob:p-md rounded-[16px] border border-solid border-gray-5 gap-lg">
        {stateIsEditing ? (
          <EditVoiceDetail
            key={voice?.id}
            voice={voice}
            sampleText={sampleText}
            isPlaying={isPlaying}
            isLoading={isAudioLoading}
            onSpeak={handleSpeak}
            onStop={handleStop}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        ) : (
          <ViewVoiceDetail
            key={voice?.id}
            voice={voice}
            sampleText={sampleText}
            isPlaying={isPlaying}
            isLoading={isAudioLoading}
            onSpeak={handleSpeak}
            onStop={handleStop}
            onEdit={handleEdit}
          />
        )}
      </div>
    </PageLayout>
  );
}
